import { BasePagination } from "./commonInterface.interface";
import type {
  CommonLocation,
  CommonPageResponse,
  CommonResponse,
  LEVEL_TYPE,
  PageResponse,
} from "./commonInterface.interface";
import { CVVType } from "pages/reports/view/cvv/interface";

export type GetReportRequest = BasePagination;

export type Interval = "DAILY" | "WEEKLY" | "MONTHLY";

interface ReportRecorderGroup {
  recorderGroupId: number;
  name: string;
  username: string;
  email: string;
  description: string;
  recorderCount: number;
  recorders: {
    id: number;
    recorderId: string;
    name: string;
    type: string;
  }[];
}

interface ReportSystem {
  mergedSystemId: string;
  mergedSystemName: string;
  recorders: {
    id: number;
    recorderId: string;
    name: string;
    type: string;
    model: string;
    modelName: string;
    cameraCount: 0;
  }[];
}

export interface ReportList {
  id: string;
  type: string;
  title: string;
  recorder: string[];
  createdBy: string;
  createdAt: Date;
  isEnable: boolean;
  recorderTotalCount: number;
  installationRecorder?: string;
  accountName: string;
  accountNumber: string;
  systems: ReportSystem[] | undefined;
  recorderGroups: ReportRecorderGroup[];
  userId: string;
}

interface GetReportListResult {
  typeInfos: TypeInfoList[];
  reports: ReportList[] | CPReportDataRow[];
}
export interface GetReportListResposne extends CommonPageResponse {
  result: GetReportListResult;
}

export interface CommonReportResponse<T = any> extends CommonResponse {
  result: T;
}

export interface NotifyUser {
  userId: string;
  isEmailEnable: boolean;
}

export interface NotifyUserGroup {
  userGroupId: string;
  isEmailEnable: boolean;
}

export interface NotifyStep {
  dayTime: string;
  nightTime: string;
  intervalType: string;
  weeklyValue: boolean[];
  monthlyValue: number;
  isEndOfMonth: boolean;
  users: Array<NotifyUser>;
  userGroups: Array<NotifyUserGroup>;
}

export interface PreferenceStep {
  title: string;
  host: string;
  description: string;
  isEnable?: boolean;
  userId: string | undefined;
}

export interface RecorderStep {
  autoApply?: boolean;
  systems: string[];
  recorderGroupId: number[];
}

interface AccountStep {
  autoApply?: boolean;
  cpAccountIds: string[];
  euAccountIds: string[];
}

export interface getPreferenceResponse extends CommonResponse {
  result: PreferenceStep;
}

export interface getReportInfoResponse extends CommonResponse {
  result:
    | HealthReport
    | InstallReport
    | CVVReport
    | ThumbnailReport
    | ReportPreference
    | CPReport;
}
interface ReportPreference {
  preferences: PreferenceStep;
}

export interface CPReport {
  account: AccountStep;
  notification: NotifyStep;
  preferences: PreferenceStep;
}
export interface getReoprtDetailPerformanceResponse extends CommonResponse {
  result: PerformanceData[];
}

export interface getReoprtDetailRemoteAccessResponse extends CommonResponse {
  result: ReportHealthDetailRemote;
}

export interface HealthReport {
  recorder: RecorderStep;
  notification: NotifyStep;
  preferences: PreferenceStep;
}

export interface InventoryReport {
  recorder: RecorderStep;
  notification: NotifyStep;
  preferences: PreferenceStep;
}
export interface InstallReport {
  recorder: {
    recorderId: string;
  };
  information: {
    customerName: string;
    customerPhoneNumber: string;
    customerLocation: string;
    installerName: string;
    installerPhoneNumber: string;
    installerCompany: string;
    installerCompanyPhoneNumber: string;
    installerCompanyAddress: string;
    installationDate: string;
    deviceSerial: string;
    deviceDescription: string;
    analogCameraModels: [];
  };
  updatable?: boolean;
  targetAccountId?: string;
  title: string;
}

export interface CameraDetailInfo {
  cameraId: string;
  channel: number;
}

export interface CameraStep {
  mergedRecorderId?: string;
  recorderId: string;
  camera: CameraDetailInfo[];
}

export interface CVVReport {
  recorderCameraId: CameraStep[];
  recorder: RecorderStep;
  notification: NotifyStep;
  preferences: PreferenceStep;
}
interface ScheduleStep {
  schedule: {
    timeString: string;
    days: boolean[];
  }[];
}

export interface ThumbnailReport extends ScheduleStep {
  recorderCameraId: CameraStep[];
  recorder: RecorderStep;
  notification: NotifyStep;
  preferences: PreferenceStep;
  storage: number;
}

interface TypeInfoList {
  type: string;
  typeCount: number;
}

export interface ReportHealthList {
  id: number;
  recorderId: string;
  recorderName: string;
  recorderModel: string;
  cloudSystemId: string;
  mergedSystemId: string;
  mergedSystemName: string;
  firmwareVersion: string;
  type: string;
  recorderCount: number;
  cameraCount: number;
  onlineStatusCount: number;
  host: string;
  createBy: string;
  createByEmail: string;
  total: number;
  videoLoss: number;
  alertCount: number;
  accountName: string;
  accountId: string;
  accountNumber: string;
  status: {
    status: string;
    alertId: string;
  }[];
  cloudStorage: {
    totalStorage: number;
    usedStorage: number;
  };
  storages: {
    name: string;
    free: number;
    total: number;
    reserved: number;
  }[];
  licenseType?: string | null;
}

interface ReportInventoryRecorderList {
  recorderId: string;
  type: string;
  model: string;
  location?: CommonLocation;
  installedDate: string;
  firmwareVersion: string;
  macAddr: string;
  licenseType?: string | null;
  cameraCount: number;
  installerName: string;
  title: string;
  accountName: string;
  accountNumber: string;
  isCloudCam?: boolean;
}

export interface GetReportHealthList extends CommonPageResponse {
  result: {
    healthy: number;
    systemAlerts: number;
    diskAlerts: number;
    videoLossAlerts: number;
    healthReports: ReportHealthList[];
  };
}

export interface GetReportInvetoryRecorderList extends CommonPageResponse {
  result: ReportInventoryRecorderList[];
  count: number;
}
export interface GetReportInventoryCameraList extends CommonPageResponse {
  result: ReportInventoryCameraRow[];
}

//Recorder정보는 앞화면의 List 에서 Prop으로 전달받음.
//TODO  alert/performance 를 전체를 들고올것인지 개별로 호출할 것인지 확인필요.
export interface ReportHealthDetailRecorderAlert {
  alertId: string;
  alertHistoryId: string;
  alertName: string;
  isCritical: true;
  status: string;
  dateTime: string;
  type: string;
  cameraChannel: number;
  cameraName: string;
  cameraId: string;
  alertTrigger: string;
  alertReason: string;
  eventDate: string;
  timezone: string;
  storageName?: string;
}

export interface ReportHealthDetailRecorder {
  onlineStatus: boolean;
  totalAlerts: number;
  alerts: ReportHealthDetailRecorderAlert[];
}
export interface ReportHealthDetailCamera {
  lastUpdate?: string;
  totalCamera: number;
  videoLoss: number;
  cameras: ReportHealthDetailCameraData[];
}
export interface ReportHealthDetailCameraData {
  channel: number;
  name: string;
  videoLoss: boolean;
  thumbnailUrl: string;
  key?: number | string;
  covert: boolean;
  permission: boolean;
}

export interface PerformanceData {
  cpu: number;
  memory: number;
  timestamp: string;
}
export interface ReportHealthDetailPerformance {
  cpuPercentage: number;
  memoryPercentage: number;
  performance: PerformanceData[];
}
export interface RemoteAccessData {
  infos: RemoteAccessInfoData[];
  name: string;
  sumCount: number;
  timezone: string;
}

export interface RemoteAccessInfoData {
  count: number;
  timestamp: string;
}
export interface ReportHealthDetailRemote {
  userTotalCount: number;
  users: RemoteAccessData[];
}

/**
 * User ----
 */
export interface ReportHealthDetailUser {
  totalUsers: number;
  users: UsersInterface[];
}

interface UserGroupsInterface {
  name: string;
  description: string;
  groupId: string;
  enableDelete: boolean;
}
export interface UsersInterface {
  id: string;
  name: string;
  email: string;
  userGroups: UserGroupsInterface[];
  key?: number | string;
}

/**
 * -------
 */
export interface ReportHealthDetailStorage {
  name: string;
  free: number;
  total: number;
  reserved: number;
}

export interface ReportHealthDetailStorageResponse extends CommonResponse {
  result: ReportHealthDetailStorage[];
}

export interface ReportHealthDetailCloudStorage {
  totalStorage: number;
  usedStorage: number;
}

export interface ReportDetailRecorderInfo {
  recorderName: string;
  recorderModel: string;
  installedDate: string;

  location: string;
}

export interface GetReportHealthDetail {
  error: number;
  errorString: string;
  result: {
    recorderName: string;
    recorderModel: string;
    installedDate: string;
    title: string;
    accountId: string;
    accountName: string;
    accountNumber: string;
    accountLevel: LEVEL_TYPE;
    location: CommonLocation;
    recorderHealth: ReportHealthDetailRecorder;
    cameraHealth: ReportHealthDetailCamera;
    performance: PerformanceData[];
    remoteAccess: ReportHealthDetailRemote;
    storages: ReportHealthDetailStorage[];
    users: ReportHealthDetailUser;
    cloudStorage: ReportHealthDetailCloudStorage;
  };
}

/**
 * Health
 */
export interface RecorderHealthRow {
  type: string;
  displayType: string;
  title: string;
  status: string;
  accessType?: string;
  totalStorage: number;
  usedStorage: number;
  freeStorage: number;
  totalCamera: number;
  lossCamera: number;
  version: string;
  cloud?: boolean;
  cloudSystemId: string;
  spectrumRecoders?: number;
  recorderCount: number;
  onlineStatusCount: number;
  mergedSystemId: string;
  mergedSystemName: string;
  recorderId: string;
  recorderModel: string;
  recorderName: string;
  accountName?: string;
  accountId?: string;
  accountNumber?: string;
  licenseType?: string | null;
  isCloudCam?: boolean;
}

export interface RecorderHealthDetailRow {
  [string: string]: string;
}
/**
 * report list -> recorder health -> detail
 */
export interface RecorderHealthMoreDetailInterface {
  label: string | React.ReactNode;
  contentLabel?: string;
  value: string | number | React.ReactNode;
  img: string;
  icon: any;
  status?: string;
  subLabel?: string;
}

/**
 * Inventory
 */
export interface ReportInventoryRecorderRow {
  isCloudCam?: boolean;
  mergedSystemId: string;
  recorderId: string;
  type: string;
  displayType: string;
  model: string;
  location?: ReportLocation;
  installedDate: string;
  firmwareVersion: string;
  macAddr: string;
  licenseType?: string | null;
  cameraCount: number;
  serialNo: string;
  installerName: string;
  title: string;
  name: string;
  timezone: string;
  mergedSystemName: string;
  accountName: string;
  accountNumber: string;
  accountId: string;
  isConfig: boolean;
  recorderCount: number;
  brand: string;
  recorders: ReportInventoryRecorderRowDetail[];
}

interface ReportLocation {
  id: number;
  location: string;
  latitude: number;
  longitude: number;
  isConfirm: boolean;
}

export interface ReportInventoryRecorderRowDetail {
  location: ReportLocation;
  recorderId: string;
  name: string;
  brand: string;
  type: string;
  displayType: string;
  model: string;
  licenseType: string;
  createdAt: string;
  macAddr: string;
  cameraCount: number;
  installerName: string;
  firmwareVersion: string;
  timezone: string;
}
export interface ReportInventoryCameraRow {
  recorderName: string;
  mergedSystemName: string;
  name: string;
  manufacturer: string;
  model: string;
  macAddr?: string;
  storage: Storage[];
  streams: Stream[];
  resolution: string;
  cameraId: string;
  channel: number;
  id: string;
  totalStorage: number;
  accountName: string;
  accountNumber: string;
  accountId: string;
  version: string;
}

export interface Storage {
  free: number;
  name: string;
  reserved: number;
  total: number;
}

interface Stream {
  codec: string;
  encoderIndex: number;
  resolution: string;
}

/**
 * default report
 */
export interface ReportDataRow {
  id: string;
  type: string;
  title: string;
  recorder: string;
  createdBy: string;
  email: string;
  createdAt: string; // Date 형식으로 추후 변경 필요
  enable: boolean | null;
  warning?: boolean;
  accountName: string;
  accountNumber: string;
  installationRecorder?: string;
  recorderTotalCount: number;
  userId: string;
  systems: ReportSystem[] | undefined;
  isDefault: boolean;
  isUpdatable: boolean;
}

export interface CPReportDataRow {
  id: string;
  type: string;
  autoApply: boolean;
  title: string;
  createdBy: string;
  createdAt: string;
  email: string;
  isEnable: boolean;
  accountCount: number;
  recorderCount: number;
  cameraCount: number;
  isDefault: boolean;
  accounts?: {
    accountName: string;
    accountId: string;
  }[];
  systems?: {
    mergedSystemId: string;
    mergedSystemName: string;
  }[];
  recorderTotalCount: number;
  isUpdatable: boolean;
}

/**
 * Thumbnail
 */
export interface ReportThumbnailFilterRow {
  [string: string]: string;
}

export interface InstallationView {
  recorderInfo: {
    name: string;
    installationDate: string;
    location: string;
    model: string;
    timezone: string;
  };
  cameraInfo: {
    model: string;
    name: string;
    channel: number;
    url: string;
    thumbnailUrl: string;
    installationStatus: string;
    covert: boolean;
  }[];
  installationReport: {
    id: string;
    customerName: string;
    customerPhoneNumber: string;
    customerLocation: string;
    installerName: string;
    installerPhoneNumber: string;
    installerCompany: string;
    installerCompanyPhoneNumber: string;
    installerCompanyAddress: string;
    installationDate: string;
    deviceSerial: string;
    deviceDescription: string;
    targetAccountId: string;
    targetAccountName: string;
    targetAccountNumber: string;
    analogCameraModels: [];
    updatable: boolean;
  };
  title?: string;
}

export interface InstallationViewResponse extends CommonResponse {
  result: InstallationView;
}

export interface DateTime {
  date: string;
  time: string[];
}

export interface ThumbnailDateTimeResponse extends CommonResponse {
  result: DateTime[];
}

export interface ThumbnailQueryString extends BasePagination {
  date: string;
  time: string;
}

export interface ThumbnailFilterQueryString extends BasePagination {
  recorderId: string;
  cameraId: string;
  channel: number;
  date: string;
}

export interface ThumbnailData {
  id: string;
  timestamp: string;
  timezone: string;
  url: string;
  errorType: string;
}

export interface ThumbnailViewItem extends ThumbnailData {
  cameraName: string;
  recorderName: string;
}

export interface ThumbnailFilterViewItem {
  thumbnails: ThumbnailData[];
  cameraName: string;
  recorderName: string;
}

export interface ThumbnailViewResponse extends CommonResponse {
  result: ThumbnailViewItem[];
  page: PageResponse;
}

export interface ThumbnailViewFilterResponse extends CommonResponse {
  result: ThumbnailFilterViewItem[];
  page: PageResponse;
}

interface ThumbnailRecorderList {
  id: string;
  name: string;
  type: string;
}

export interface ThumbnailRecorderResponse extends CommonResponse {
  result: ThumbnailRecorderList[];
}

interface ThumbnailCameraList {
  id: string;
  name: string;
  channel: number;
}

export interface ThumbnailCameraResponse extends CommonResponse {
  result: ThumbnailCameraList[];
}

export interface ThumbnailFilterDateResponse extends CommonResponse {
  result: string[];
}

export interface CvvQueryString extends BasePagination {
  cvvEnabled: boolean;
  recorderId: string;
}

export enum CVV_ERROR_STATUS {
  THUMBNAIL_NOT_AVAILABLE = "ThumbnailNotAvailable",
  CAMERA_INVALID = "CameraInvalid",
  CAMERA_OFFLINE = "CameraOffline",
  CAMERA_UNAUTHORIZED = "CameraUnauthorized",
  CAMERA_NOT_RECORDING = "CameraNotRecording",
  JPEG_ENCODING_FAILED = "JpegEncodingFailed",
  RECORDER_NOT_RESPONDING = "RecorderNotResponding",
  RECORDER_AUTH_FAIL = "RecorderAuthFail",
  THUMBNAIL_ERROR = "ThumbnailUploadFail",
  UPDATE_SCHEDULED = "UpdateScheduled",
  VIDEO_LOSS = "VideoLoss",
  CAMERA_RESPONSE_FAILURE = "CameraResponseFailure",
}
export interface CvvItem {
  [key: string]: string | boolean;
  id: string;
  url: string;
  date: string;
  blur: boolean;
  dust: boolean;
  glare: boolean;
  spider: boolean;
  tampered: boolean;
  similarity: boolean;
  abnormal: boolean;
  thumbnailId: string;
  errorStatus: CVV_ERROR_STATUS;
  isAnalyzing: boolean;
}

export interface CVVPairItem {
  referenceThumbnail: CvvItem;
  eventThumbnail: CvvItem;
}

export interface DayNightItem {
  [key: string]: string | number | CVVPairItem | CvvItem | boolean;
  id: string;
  isCvv: boolean;
  isWarning: boolean;
  name: string;
  cameraId: string;
  channel: number;
  day: CVVPairItem;
  night: CVVPairItem;
  covert: boolean;
  permission: boolean;
}
export interface CvvViewListItem {
  isWarning: boolean;
  date: string;
  usageCameras: number;
  totalCameras: number;
  warningCameras: number;
  cvvCameras: number;
  recorderType: string;
  timezone: string;
  offset: number;
  location: {
    id: number;
    location: string;
    latitude: number;
    longitude: number;
  };
  cameras: DayNightItem[];
}

export interface CvvViewResponse extends CommonResponse {
  result: CvvViewListItem;
  page: PageResponse;
}

export interface CvvRecorderResponse extends CommonResponse {
  result: ThumbnailRecorderList[];
}

export interface CVVReferenceUpdate {
  recorderId: string;
  cameraId: string;
  channel: 0;
  srcId: string;
  dstId: string;
}

export interface CvvCheckNowRequest {
  accountId: string;
  reportId: string;
  recorderId: string;
  cameraId: string;
  dayNight: CVVType;
}

export type CvvCheckNowRequestPartial = Omit<CvvCheckNowRequest, "reportId">;

export interface CvvCheckNowResponse {
  error: number;
  errorString?: string;
  result: string;
}

export interface ExportEmailRequest {
  email: string[];
  title: string;
  message: string;
  reportId: string;
}

type ExportEmailSortDto = Pick<BasePagination, "sortType" | "sortDirection">;

export type ExportEmailPayLoad = ExportEmailRequest & {
  recorderSortDto?: ExportEmailSortDto;
  cameraSortDto?: ExportEmailSortDto;
};

export interface SpectrumLicense {
  key: string;
  support: string;
  name: string;
  serial: string;
  hwid: string;
  count: string;
  class: string;
  version: string;
  brand: string;
  expiration: string;
  signature2: string;
  company: string;
  isValid: string;
  servername: string;
}

interface SpectrumSystemStatus {
  type: string;
  systemSpec: SpectrumSystemSpec;
  licenses?: SpectrumLicense[];
  statistics: SystemSpectrumUsage;
  licenseType?: string | null;
  enterprise?: SpectrumEnterprise[];
}

export interface SpectrumEnterprise {
  channelPartner: {
    id: string;
    name: string;
  };
  organization: {
    id: string;
    name: string;
  };
  services: SpectrumEnterpriseService[];
  state: "uninitialized" | "active" | "suspended" | "shutdown" | "autoShutdown";
}

export interface SpectrumEnterpriseService {
  id: string;
  displayName: string;
  type: string;
  quantity: number;
  used: number;
  state: "active" | "obsolete";
  description: string;
  createdByChannelPartner: string;
}

interface SpectrumSystemSpec {
  parameters: {
    cpuArchitecture: string;
    cpuModelName: string;
    networkInterfaces: string;
    physicalMemory: string;
  };
  osInfo: {
    platform: string;
    variant: string;
    variantVersion: string;
  };
}

export interface SpectrumSystemStatusRes extends CommonResponse {
  result: SpectrumSystemStatus;
}

export interface SpectrumSystemStatsRes extends CommonResponse {
  result: {
    type: string;
    statistics: SystemSpectrumUsage;
  };
}

interface SystemSpectrumUsage {
  cpu: number;
  ram: number;
  network: number;
}

export interface VmaxSystemStatus {
  id: string;
  mac: string;
  apiVersion: string;
  model: string;
  firmwareVersion: string;
  type: string;
  network: {
    ddns: boolean; // ddns <-> internet
    gateway: boolean; // gateway <->internet
    recorder: boolean; // gateway <-> recorder
    client: {
      connection: {
        max: number;
        connectedSize: number;
      };

      // spectrum
      channel?: {
        max: number;
        connectedSize: number;
      };
    };
    gatewayIp: string;
    recorderIp: string;
    txMbps: number;
    rxMbps: number;

    //spectrum
    poeMbps?: number;
    lanMbps?: number;

    // cova
    lanTxMbps?: number;
    lanRxMbps?: number;
    poeTxMbps?: number;
    poeRxMbps?: number;
  };
  system: {
    cpu: number;
    memory: number;
    swap: number;
    //spectrum
    temperature?: number;
    poe: {
      order: number;
      enable: boolean;
      online: boolean;
      watt: number;
    }[];
  };
  cameras: CameraSystemStatus[];
  displayType: string;
}

export interface CameraSystemStatus {
  channel: number;
  status: string; //online, videoLoss, offline
  txMbps: number;
  firstStreamFrame: number;
  secondStreamFrame: number;
  poe: number; // Connected POE number for IP camera
}

export interface VmaxSystemStatusRes extends CommonResponse {
  result: VmaxSystemStatus;
}
